import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ClipboardList, IndianRupee, CheckCircle2, MapPinned, ArrowRight, CalendarClock, HandCoins, Target, FileWarning, BellRing } from "lucide-react";
import { Spinner } from "../../components/common";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Progress } from "../../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { api, fmtINR, fmtDate, istDateISO } from "../../lib/api";
import { useLiveCollections } from "../../lib/liveCollections";
import { useAuth } from "../../context/AuthContext";
import { FollowUpTopCard } from "../../components/FollowUpList";
import { CollectorTargetCard } from "../../components/CollectorTargetCard";
import { FollowUpDoneDialog, FollowUpDueTodayDialog, FollowUpRowActions, FollowUpStatusBadge, RepeatedBadge, useDueTodayPopup } from "../../components/FollowUpActions";

const OUTCOME_SHORT = {
  preferred_date: "Preferred date", next_visit: "Next visit", closed: "Was closed", absent: "Owner absent",
  locked: "Locked", no_payment: "No payment", refused: "Refused", partial_payment: "Partial paid",
};

export default function CollectorHome() {
  const { code } = useParams();
  const { auth } = useAuth();
  const [data, setData] = useState(null);
  const [todo, setTodo] = useState(null);
  const [partials, setPartials] = useState(null);
  const [wardProgress, setWardProgress] = useState(null);
  const [pendingDocs, setPendingDocs] = useState(null);

  const loadTodo = useCallback(() => {
    api.get(`/t/${code}/field/todo`).then((r) => setTodo(r.data)).catch(() => setTodo({ items: [], total: 0 }));
  }, [code]);

  // Phase 149: today's figures, partial trackers and ward progress follow the live ledger
  const loadFigures = useCallback(() => {
    api.get(`/t/${code}/dashboard/collector`).then((r) => setData(r.data)).catch(() => {});
    api.get(`/t/${code}/partial-payments`, { params: { status: "open" } }).then((r) => setPartials(r.data)).catch(() => setPartials([]));
    api.get(`/t/${code}/field/ward-progress`).then((r) => setWardProgress(r.data)).catch(() => setWardProgress([]));
  }, [code]);
  useLiveCollections(code, () => { loadFigures(); loadTodo(); });

  useEffect(() => {
    loadFigures();
    loadTodo();
    api.get(`/t/${code}/field/pending-docs`).then((r) => setPendingDocs(r.data)).catch(() => setPendingDocs(null));
    // log GPS location once per session (best effort)
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => api.post(`/t/${code}/field/location`, {
          latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy,
        }).catch(() => {}),
        () => {}, { timeout: 5000 }
      );
    }
  }, [code, loadTodo, loadFigures]);

  // Phase 196: once-a-day "You have X follow-up visits due today" popup + Done (with reason) straight from the popup
  const due = useDueTodayPopup(code, auth?.user?.id, true);
  const [popupDone, setPopupDone] = useState(null);
  const afterAction = () => { loadTodo(); due.reload(); };

  if (!data) return <Spinner />;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold font-heading">Namaste, {auth?.user?.name?.split(" ")[0]}</h1>
        <p className="text-sm text-muted-foreground">Your field summary for today</p>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="overview" data-testid="collector-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="partials" data-testid="collector-tab-partials">
            Partial payments{partials && partials.length > 0 ? ` (${partials.length})` : ""}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="partials" className="space-y-3 mt-4">
          {!partials ? <Spinner /> : partials.length === 0 ? (
            <Card className="shadow-sm">
              <CardContent className="p-6 text-center text-sm text-muted-foreground" data-testid="partials-empty-state">
                <HandCoins className="h-6 w-6 mx-auto mb-2 opacity-60" />
                No open partial payments. When you collect part of a demand, the balance is tracked here for your next visit.
              </CardContent>
            </Card>
          ) : (
            partials.map((p) => (
              <Card key={p.id} className={`shadow-sm ${p.overdue ? "border-red-300" : ""}`} data-testid="partial-payment-item">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <Link to={`/${code}/collector/property/${p.property_id}`} className="min-w-0">
                      <p className="text-sm font-medium truncate"><span className="font-mono">{p.pin}</span> • {p.owner_name || "-"}</p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        Paid {fmtINR(p.paid_amount)} on {p.created_at ? fmtDate(istDateISO(new Date(p.created_at))) : "-"} • Receipt {p.receipt_number}
                      </p>
                    </Link>
                    {p.overdue && <Badge variant="destructive" className="text-[10px] shrink-0">Overdue</Badge>}
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-3 text-center">
                    <div className="bg-muted rounded-lg py-2">
                      <p className="text-[11px] text-muted-foreground">Outstanding</p>
                      <p className="text-sm font-semibold tabular-nums text-destructive" data-testid="partial-outstanding-amount">{fmtINR(p.current_outstanding)}</p>
                    </div>
                    <div className="bg-muted rounded-lg py-2">
                      <p className="text-[11px] text-muted-foreground">Promised date</p>
                      <p className="text-sm font-semibold" data-testid="partial-promised-date">{p.promised_date || "—"}</p>
                    </div>
                  </div>
                  <Button asChild size="sm" variant="outline" className="w-full mt-3" data-testid={`partial-collect-${p.pin}`}>
                    <Link to={`/${code}/collector/property/${p.property_id}`}>Collect balance <ArrowRight className="h-3.5 w-3.5 ml-1" /></Link>
                  </Button>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        <TabsContent value="overview" className="space-y-4 mt-4">
      <Card className="bg-primary text-primary-foreground shadow-md">
        <CardContent className="p-5">
          <p className="text-sm opacity-90">Today's collection</p>
          <p className="text-3xl font-semibold font-heading tabular-nums mt-1" data-testid="collector-today-amount">{fmtINR(data.today_amount)}</p>
          <p className="text-xs opacity-80 mt-1">{data.today_count} receipts issued today • {fmtINR(data.total_amount)} lifetime</p>
        </CardContent>
      </Card>

      {/* Phase 139: my monthly target vs what I've collected so far */}
      <CollectorTargetCard code={code} />

      <div className="grid grid-cols-2 gap-3">
        {[
          { label: "Pending visits", value: data.pending, icon: ClipboardList, testId: "collector-kpi-pending" },
          { label: "Visited", value: data.visited, icon: MapPinned, testId: "collector-kpi-visited" },
          { label: "Collected", value: data.collected, icon: CheckCircle2, testId: "collector-kpi-collected" },
          { label: "Total receipts", value: data.total_count, icon: IndianRupee, testId: "collector-kpi-receipts" },
        ].map((k) => (
          <Card key={k.label} className="shadow-sm" data-testid={k.testId}>
            <CardContent className="p-4">
              <k.icon className="h-5 w-5 text-primary mb-2" />
              <p className="text-2xl font-semibold tabular-nums">{k.value}</p>
              <p className="text-xs text-muted-foreground">{k.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Button asChild size="lg" className="w-full h-12" data-testid="collector-goto-assigned">
        <Link to={`/${code}/collector/assigned`}>View assigned properties <ArrowRight className="h-4 w-4 ml-2" /></Link>
      </Button>

      {/* Phase 105: where to go first — highest balances due in my area */}
      <FollowUpTopCard code={code} limit={5} />

      {pendingDocs && pendingDocs.items.length > 0 && (
        <Card className="shadow-sm border-l-4 border-l-amber-500" data-testid="pending-docs-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-heading flex items-center gap-2">
              <FileWarning className="h-4 w-4 text-amber-600" /> Documents due for collection
            </CardTitle>
            <p className="text-[11px] text-muted-foreground">
              You promised to collect documents for these cases — tap to attach them (clear the date once collected)
            </p>
          </CardHeader>
          <CardContent className="pt-0 space-y-2">
            {pendingDocs.items.slice(0, 6).map((it) => (
              <Link key={it.id} to={`/${code}/collector/field`}
                onClick={() => sessionStorage.setItem("open_docs_target", `${it.kind}:${it.id}`)}
                className="flex items-center justify-between gap-2 rounded-lg border px-3 py-2 hover:bg-muted/60 active:scale-[0.99] transition-transform"
                data-testid={`pending-doc-${it.case_id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-mono font-medium">{it.case_id}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {it.owner_name || it.pin || ""}{it.files_count > 0 ? ` • ${it.files_count} file(s) attached` : " • no files yet"}
                  </p>
                </div>
                <Badge variant={it.overdue_days > 0 ? "destructive" : "secondary"} className="shrink-0 text-[10px]"
                  data-testid={`pending-doc-badge-${it.case_id}`}>
                  {it.overdue_days > 0 ? `${it.overdue_days} day${it.overdue_days === 1 ? "" : "s"} overdue` : "Due today"}
                </Badge>
              </Link>
            ))}
            {pendingDocs.items.length > 6 && (
              <p className="text-[11px] text-muted-foreground text-center">+{pendingDocs.items.length - 6} more in Field ops</p>
            )}
          </CardContent>
        </Card>
      )}

      {wardProgress && wardProgress.length > 0 && (
        <Card className="shadow-sm" data-testid="ward-targets-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-heading flex items-center gap-2">
              <Target className="h-4 w-4 text-primary" /> Ward collection targets
            </CardTitle>
            <p className="text-[11px] text-muted-foreground">Due vs collected in your wards — tap a ward to see its properties</p>
          </CardHeader>
          <CardContent className="pt-0 space-y-3">
            {wardProgress.slice(0, 8).map((w) => (
              <Link key={w.ward_id || "none"} to={`/${code}/collector/assigned${w.ward_id ? `?ward=${w.ward_id}` : ""}`}
                className="block rounded-lg border px-3 py-2.5 hover:bg-muted/60 active:scale-[0.99] transition-transform"
                data-testid={`ward-target-${w.ward_name}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium truncate">
                    {w.ward_name}
                    {w.zone_name && <span className="text-[11px] font-normal text-muted-foreground"> • {w.zone_name}</span>}
                  </p>
                  <span className="text-[11px] text-muted-foreground shrink-0">{w.properties} prop{w.properties === 1 ? "" : "s"}</span>
                </div>
                <div className="flex items-center gap-2 mt-1.5">
                  <Progress value={w.pct} className="h-2 flex-1" />
                  <span className="text-[11px] font-semibold tabular-nums w-10 text-right">{w.pct}%</span>
                </div>
                <div className="flex items-center justify-between mt-1 text-[11px]">
                  <span className="text-muted-foreground">Collected {fmtINR(w.paid)} of {fmtINR(w.total)}</span>
                  <span className={`font-semibold tabular-nums ${w.balance > 0 ? "text-destructive" : "text-emerald-600"}`}>
                    {w.balance > 0 ? `${fmtINR(w.balance)} due` : "Fully collected"}
                  </span>
                </div>
              </Link>
            ))}
          </CardContent>
        </Card>
      )}

      {todo && todo.total > 0 && (
        <Card className="shadow-sm border-amber-200" data-testid="collector-todo-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-heading flex items-center justify-between gap-2 flex-wrap">
              <span className="flex items-center gap-2"><CalendarClock className="h-4 w-4 text-amber-600" /> To-Do — follow-up visits ({todo.total})</span>
              <span className="flex items-center gap-1.5 text-[11px] font-normal">
                {todo.summary?.overdue > 0 && <Badge variant="destructive" className="text-[10px]" data-testid="todo-overdue-count">{todo.summary.overdue} overdue</Badge>}
                {todo.summary?.due_today > 0 && <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-800 border-indigo-200" data-testid="todo-due-today-count">{todo.summary.due_today} due today</Badge>}
                {todo.summary?.due_today > 0 && (
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-[11px]" onClick={() => due.setOpen(true)} data-testid="todo-show-popup"><BellRing className="h-3.5 w-3.5 mr-1" /> Today's list</Button>
                )}
              </span>
            </CardTitle>
            <p className="text-[11px] text-muted-foreground">All {todo.total} follow-ups — scroll to see every one. Mark each Done with a reason, or reschedule it.</p>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1" data-testid="todo-list" data-count={todo.items.length}>
              {todo.items.map((v) => (
                <div key={v.id} className={`border rounded-lg px-3 py-2 ${v.fu_status === "overdue" ? "border-red-300 bg-red-50/60" : v.fu_status === "due_today" ? "border-indigo-200 bg-indigo-50/40" : ""}`} data-testid="todo-item" data-status={v.fu_status}>
                  <div className="flex items-start justify-between gap-2">
                    <Link to={`/${code}/collector/property/${v.property_id}`} className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">
                        <span className="font-mono">{v.pin}</span> • {v.owner_name || "-"}
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        {OUTCOME_SHORT[v.visit_outcome] || v.visit_outcome_label || v.visit_outcome}
                        {v.follow_up_date ? ` • ${fmtDate(v.follow_up_date)}` : " • no date set"}
                        {v.balance_due ? ` • ${fmtINR(v.balance_due)} due` : ""}
                        {v.reschedule_count > 0 ? ` • rescheduled ×${v.reschedule_count}` : ""}
                      </p>
                      {v.notes && <p className="text-[11px] text-muted-foreground truncate">{v.notes}</p>}
                      <div className="flex flex-wrap items-center gap-1 mt-1">
                        <FollowUpStatusBadge status={v.fu_status} label={v.fu_status_label} testId={`todo-status-${v.pin}`} />
                        {v.repeated && <RepeatedBadge count={v.follow_up_count} testId={`todo-repeated-${v.pin}`} />}
                      </div>
                    </Link>
                    <FollowUpRowActions code={code} item={v} onChanged={afterAction} compact testIdPrefix="todo" />
                  </div>
                </div>
              ))}
            </div>
            <Link to={`/${code}/collector/follow-up?tab=visits`} className="mt-2 inline-flex items-center gap-1 text-xs text-primary font-medium" data-testid="todo-open-report">
              Open my follow-up visits report <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </CardContent>
        </Card>
      )}

      <FollowUpDueTodayDialog data={due.data} open={due.open} onClose={() => due.setOpen(false)}
        propertyPath={(v) => `/${code}/collector/property/${v.property_id}`} onAct={(v) => setPopupDone(v)} />
      <FollowUpDoneDialog code={code} item={popupDone} open={!!popupDone} onClose={() => setPopupDone(null)} onDone={afterAction} testId="popup-done" />

      <div className="grid grid-cols-2 gap-3">
        <Card className="shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-xl font-semibold tabular-nums">{data.reassessments}</p>
            <p className="text-xs text-muted-foreground">Reassessments filed</p>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-xl font-semibold tabular-nums">{data.surveys}</p>
            <p className="text-xs text-muted-foreground">Surveys submitted</p>
          </CardContent>
        </Card>
      </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
